(function () {
  'use strict';

  window.Universe = window.Universe || {};

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/' + type },
    };
  }

  function debounce(func, wait, immediate) {
    let timeout;
    return function () {
      const context = this;
      const args = arguments;
      const later = () => {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }

  function throttle(fn, delay) {
    let lastCall = 0;
    return function () {
      const now = new Date().getTime();
      if (now - lastCall < delay) return;
      lastCall = now;
      return fn.apply(this, arguments);
    };
  }

  function scrollToY(targetY = 0) {
    window.scrollTo({
      top: targetY,
      behavior: 'smooth',
    });
  }

  function scrollToElement(target, offset = 0) {
    const box = target.getBoundingClientRect();
    const scrollTop = (window.scrollY || document.documentElement.scrollTop) + box.top - offset;
    scrollToY(scrollTop);
  }

  const scrollDefaultOptions = {
    threshold: 0,
    rootMargin: '0px 0px -10% 0px',
  };

  const visibilityCallbacks = new Map();
  const pendingDelays = new Map();

  function onVisible(element, callback) {
    if (!visibilityCallbacks.has(element)) {
      visibilityCallbacks.set(element, []);
    }
    visibilityCallbacks.get(element).push(callback);
  }

  function offVisible(element, callback) {
    if (!visibilityCallbacks.has(element)) return;

    if (callback) {
      const callbacks = visibilityCallbacks.get(element);
      const index = callbacks.indexOf(callback);
      if (index > -1) callbacks.splice(index, 1);
      if (callbacks.length === 0) visibilityCallbacks.delete(element);
    } else {
      visibilityCallbacks.delete(element);
    }
  }

  let scrollObserver = null;

  function runScrollCallbacks(element) {
    if (visibilityCallbacks.has(element)) {
      visibilityCallbacks.get(element).forEach((cb) => {
        cb(element);
      });
      visibilityCallbacks.delete(element);
    }
    element.dispatchEvent(new CustomEvent('scroll:visible', { bubbles: true }));
  }

  function createScrollObserver(options) {
    const config = Object.assign({}, scrollDefaultOptions, options || {});

    return new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target;
          const delay = target.dataset.scrollDelay || 0;

          if (delay > 0) {
            const timeoutId = setTimeout(
              () => {
                pendingDelays.delete(target);
                target.classList.add('is-visible');
                runScrollCallbacks(target);
              },
              parseFloat(delay) * 1000,
            );
            pendingDelays.set(target, timeoutId);
          } else {
            target.classList.add('is-visible');
            runScrollCallbacks(target);
          }

          scrollObserver.unobserve(target);
        }
      });
    }, config);
  }

  const scrollTransitions = {
    observe: (target, options) => {
      if (!scrollObserver) {
        scrollObserver = createScrollObserver(options);
      }
      scrollObserver.observe(target);
    },

    unobserve: (target) => {
      if (scrollObserver) {
        scrollObserver.unobserve(target);
      }
      if (pendingDelays.has(target)) {
        clearTimeout(pendingDelays.get(target));
        pendingDelays.delete(target);
      }
      offVisible(target);
    },

    show: (target) => {
      target.classList.add('is-visible');
      runScrollCallbacks(target);
    },

    hide: (target) => {
      target.classList.remove('is-visible');
    },

    onVisible: onVisible,
    offVisible: offVisible,
  };

  const gsap = window.gsap;

  const easeNone = 'none';
  const easePower4In = 'power4.in';
  const easePower4Out = 'power4';
  const easePower4 = 'power4.inOut';
  const easePower3In = 'power3.in';
  const easePower3Out = 'power3';
  const easePower3 = 'power3.inOut';
  const easePower2 = 'power2.inOut';
  const easeExpo = 'expo.inOut';

  const animProp = (prop, immediate = true, resetValue = 0) => {
    return immediate ? resetValue : prop;
  };

  const prefersReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  function horizontalLoop(items, config) {
    items = gsap.utils.toArray(items);
    config = config || {};
    const tl = gsap.timeline({
      repeat: config.repeat,
      paused: config.paused,
      defaults: { ease: 'none' },
      onReverseComplete: () => {
        tl.totalTime(tl.rawTime() + tl.duration() * 100);
      },
    });
    const length = items.length;
    const startX = items[0].offsetLeft;
    const times = [];
    const widths = [];
    const xPercents = [];
    let curIndex = 0;
    const pixelsPerSecond = (config.speed || 1) * 100;
    const snap = config.snap === false ? (v) => v : gsap.utils.snap(config.snap || 1);
    let totalWidth, curX, distanceToStart, distanceToLoop, item;
    gsap.set(items, {
      xPercent: (i, el) => {
        const w = (widths[i] = parseFloat(gsap.getProperty(el, 'width', 'px')));
        xPercents[i] = snap((parseFloat(gsap.getProperty(el, 'x', 'px')) / w) * 100 + gsap.getProperty(el, 'xPercent'));
        return xPercents[i];
      },
    });
    gsap.set(items, { x: 0 });
    totalWidth =
      items[length - 1].offsetLeft +
      (xPercents[length - 1] / 100) * widths[length - 1] -
      startX +
      items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], 'scaleX') +
      (parseFloat(config.paddingRight) || 0);
    for (let i = 0; i < length; i++) {
      item = items[i];
      curX = (xPercents[i] / 100) * widths[i];
      distanceToStart = item.offsetLeft + curX - startX;
      distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, 'scaleX');
      tl.to(
        item,
        { xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100), duration: distanceToLoop / pixelsPerSecond },
        0,
      )
        .fromTo(
          item,
          { xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100) },
          {
            xPercent: xPercents[i],
            duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
            immediateRender: false,
          },
          distanceToLoop / pixelsPerSecond,
        )
        .add('label' + i, distanceToStart / pixelsPerSecond);
      times[i] = distanceToStart / pixelsPerSecond;
    }
    function toIndex(index, vars) {
      vars = vars || {};
      if (Math.abs(index - curIndex) > length / 2) {
        index += index > curIndex ? -length : length;
      }
      const newIndex = gsap.utils.wrap(0, length, index);
      let time = times[newIndex];
      if (time > tl.time() !== index > curIndex) {
        vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }
      curIndex = newIndex;
      vars.overwrite = true;
      return tl.tweenTo(time, vars);
    }
    tl.next = (vars) => toIndex(curIndex + 1, vars);
    tl.previous = (vars) => toIndex(curIndex - 1, vars);
    tl.current = () => curIndex;
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.times = times;
    if (config.reversed) {
      tl.vars.onReverseComplete();
      tl.reverse();
    }
    return tl;
  }

  function normalizeValue(value, min, max, multiplier = 1) {
    if (max === min) return 0;
    return ((value - min) / (max - min)) * Math.abs(multiplier);
  }

  const carouselLazyLoad = (emblaInstance) => {
    const imagesInView = [];
    const slideNodes = emblaInstance.slideNodes();
    const imageNodes = slideNodes.map((slideNode) => {
      return slideNode.querySelector('.js-carousel-image');
    });

    const loadImageInView = (index) => {
      const imageNode = imageNodes[index];
      const slideNode = slideNodes[index];
      const src = imageNode.getAttribute('data-src');

      imageNode.src = src;
      imagesInView.push(index);

      const onLoad = () => {
        slideNode.classList.add('has-loaded');
        imageNode.removeEventListener('load', onLoad);
      };
      imageNode.addEventListener('load', onLoad);
    };

    const loadImagesInView = () => {
      emblaInstance
        .slidesInView()
        .filter((index) => !imagesInView.includes(index))
        .forEach(loadImageInView);
      return imagesInView.length === imageNodes.length;
    };

    const loadImagesInViewAndDestroyIfDone = (emblaInstance, eventName) => {
      const loadedAll = loadImagesInView();
      if (loadedAll) emblaInstance.off(eventName, loadImagesInViewAndDestroyIfDone);
    };

    return loadImagesInViewAndDestroyIfDone;
  };

  const loadAnimation = () => {
    const pageLoader = document.querySelector('.js-page-loader');

    if (pageLoader) {
      gsap.to(pageLoader, {
        opacity: 0,
        ease: 'power4.inOut',
        onComplete: () => {
          pageLoader.style.display = 'none';
          subEvents.publish(PUB_SUB_EVENTS.loaderComplete);
        },
      });
    }
  };

  const ON_CHANGE_DEBOUNCE_TIMER = 300;

  const PUB_SUB_EVENTS = {
    cartUpdate: 'cart-update',
    quantityUpdate: 'quantity-update',
    optionValueSelectionChange: 'option-value-selection-change',
    variantChange: 'variant-change',
    cartError: 'cart-error',
    mobileMenuOpen: 'mobile-menu-open',
    loaderComplete: 'loader-complete',
  };

  class SubEvents {
    constructor() {
      this.subscribers = {};
    }

    subscribe(eventName, callback) {
      if (this.subscribers[eventName] === undefined) {
        this.subscribers[eventName] = [];
      }

      this.subscribers[eventName] = this.subscribers[eventName].concat([callback]);

      return () => {
        this.subscribers[eventName] = this.subscribers[eventName].filter((cb) => {
          return cb !== callback;
        });
      };
    }

    publish(eventName, data) {
      if (this.subscribers[eventName]) {
        this.subscribers[eventName].forEach((callback) => {
          callback(data);
        });
      }
    }
  }

  const subEvents = new SubEvents();

  class InstanceManager {
    constructor() {
      this.instances = {};
    }

    add(key, instance, preserve) {
      this.instances[key] = { key: key, instance: instance, preserve: preserve };
    }

    get(key) {
      return this.instances[key] && this.instances[key].instance ? this.instances[key].instance : null;
    }

    getAll() {
      return this.instances;
    }

    remove(key) {
      const instance = this.get(key);
      if (instance) {
        !this.instances[key].preserve && instance.destroy && instance.destroy();
        this.instances[key] = null;
      }
    }

    removeAll() {
      for (const key in this.instances) {
        this.remove(key);
      }
    }

    destroy(key) {
      const entry = this.instances[key];
      const instance = this.get(key);
      const preserve = entry ? entry.preserve : false;
      if (instance) {
        !preserve && instance.destroy && instance.destroy();
      }
    }

    destroyAll() {
      for (const key in this.instances) {
        this.destroy(key);
      }
    }

    map(func) {
      const args = Array.prototype.slice.call(arguments, 1);
      for (const key in this.instances) {
        const instance = this.get(key);
        if (instance && instance[func]) {
          instance[func].apply(instance, args);
        }
      }
    }

    call(key, func) {
      const args = Array.prototype.slice.call(arguments, 2);
      const instance = this.get(key);
      if (instance && instance[func]) return instance[func].apply(instance, args);
    }
  }

  const iman = new InstanceManager();

  class LayoutManager {
    constructor() {
      this.device = 0;
      this.window = {
        width: 0,
        height: 0,
      };
      this.initialized = false;
    }

    detectApple() {
      this.isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    createRuler() {
      if (!this.isIos) {
        this.window.height = window.innerHeight;
        return;
      }
      let ruler = document.createElement('div');

      ruler.style.position = 'fixed';
      ruler.style.height = '100%';
      ruler.style.width = 0;
      ruler.style.top = 0;

      document.documentElement.appendChild(ruler);
      this.window.height = ruler.offsetHeight;

      document.documentElement.removeChild(ruler);
      ruler = null;
    }

    getWindow() {
      this.createRuler();
      this.window.width = window.innerWidth;
    }

    getDevice() {
      this.device = window.matchMedia('(min-width:1920px)').matches
        ? 3
        : window.matchMedia('(min-width:1024px)').matches
          ? 2
          : window.matchMedia('(min-width:768px)').matches
            ? 1
            : 0;
    }

    setup() {
      this.detectApple();
      this.getDevice();
      this.getWindow();
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;
      this.setup();
    }

    resize() {
      const cachedW = window.innerWidth;
      const cachedH = window.innerHeight;

      this.detectApple();
      this.getDevice();

      if (this.window.width !== cachedW) {
        this.window.width = window.innerWidth;
      }

      if (this.window.height !== cachedH) {
        this.createRuler();
      }
    }
  }

  const layout = new LayoutManager();

  if (document.readyState !== 'loading') {
    layout.init();
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        layout.init();
      },
      { once: true },
    );
  }

  // Fires callback on the first user interaction, or after `delay` ms if none occurs.
  // Use for components that are never needed until the user does something (click,
  // scroll, keydown), so their constructor cost is pushed off the critical path.
  class InitWhenVisible {
    constructor(callback, delay = 5000) {
      this._done = false;
      this._timer = setTimeout(() => this._run(callback), delay);
      this._events = ['pointerdown', 'scroll', 'keydown', 'touchstart'];
      this._handler = () => this._run(callback);
      this._events.forEach((e) => window.addEventListener(e, this._handler, { passive: true }));
      console.log('init');
    }

    _run(callback) {
      if (this._done) return;
      this._done = true;
      clearTimeout(this._timer);
      this._events.forEach((e) => window.removeEventListener(e, this._handler));
      console.log('call');
      callback();
    }
  }

  window.Universe.iman = iman;
  window.Universe.layout = layout;
  window.Universe.subEvents = subEvents;
  window.Universe.SubEvents = SubEvents;
  window.Universe.PUB_SUB_EVENTS = PUB_SUB_EVENTS;
  window.Universe.ON_CHANGE_DEBOUNCE_TIMER = ON_CHANGE_DEBOUNCE_TIMER;

  window.Universe.debounce = debounce;
  window.Universe.throttle = throttle;
  window.Universe.fetchConfig = fetchConfig;
  window.Universe.scrollToY = scrollToY;
  window.Universe.scrollToElement = scrollToElement;
  window.Universe.scrollTransitions = scrollTransitions;
  window.Universe.carouselLazyLoad = carouselLazyLoad;
  window.Universe.loadAnimation = loadAnimation;

  window.Universe.animProp = animProp;
  window.Universe.prefersReducedMotion = prefersReducedMotion;
  window.Universe.horizontalLoop = horizontalLoop;
  window.Universe.normalizeValue = normalizeValue;

  window.Universe.InitWhenVisible = InitWhenVisible;

  window.Universe.easeNone = easeNone;
  window.Universe.easePower4In = easePower4In;
  window.Universe.easePower4Out = easePower4Out;
  window.Universe.easePower4 = easePower4;
  window.Universe.easePower3In = easePower3In;
  window.Universe.easePower3Out = easePower3Out;
  window.Universe.easePower3 = easePower3;
  window.Universe.easePower2 = easePower2;
  window.Universe.easeExpo = easeExpo;
})();
